// utils/number.js

/**
 * Calculate nice min & max range with padding
 * (useful for charts / graphs)
 * 
 * @param {number[]} arr
 * @param {number} pad - padding ratio (default 6%)
 * @returns {{ min: number, max: number }}
 */
export function niceMinMax(arr, pad = 0.06) {
  const vals = (arr || []).filter(Number.isFinite);

  if (!vals.length) {
    return { min: 0, max: 1 };
  }

  let min = Math.min(...vals);
  let max = Math.max(...vals);

  // Case: all values equal
  if (min === max) {
    const bump = Math.abs(min) || 1;
    return {
      min: min - bump * pad,
      max: max + bump * pad
    };
  }

  const range = max - min;

  return {
    min: min - range * pad,
    max: max + range * pad
  };
}

const nfComma = new Intl.NumberFormat('en-US');

/**
 * Format number with comma separators
 * @param {number} n
 * @param {number} frac - number of decimal places
 * @returns {string}
 */
export function fmtComma(n, frac = 0) {
  if (!Number.isFinite(n)) return '-';

  return nfComma.format(
    frac ? Number(n).toFixed(frac) : Math.round(n)
  );
}

/**
 * Format a numeric value into a compact string with suffixes (K, M, B).
 *
 * - >= 1,000,000,000 → Billion (B)
 * - >= 1,000,000 → Million (M)
 * - Otherwise → Thousand (K)
 *
 * @param {number|string} value - The input value to format
 * @returns {string} Formatted string (e.g., "1.25B", "3.40M", "850.00K") or "N/A" if invalid
 */

export function formatNumber(value) {
  if (!value) return '-';
  const num = parseFloat(value);
  if (!num || num === 0) return '-';
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(2) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  } else {
    return (num / 1000).toFixed(2) + 'K';
  }
  // return num.toFixed(2);
}

/**
 * Format a numeric value as a percentage-like string.
 *
 * - Returns "N/A" if value is invalid
 * - Integer → no decimal places
 * - Float → rounded to 2 decimal places
 *
 * @param {number|string} value - The input value to format
 * @returns {string} Formatted string (e.g., "10", "10.25") or "N/A"
 */
export function formatPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'N/A';

  return num % 1 === 0 ? String(num) : num.toFixed(2);
}